import {
    Box,
    Card,
    Collapse,
    Divider,
    IconButton,
    Link as MuiLink,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import React, { useMemo, useState } from 'react';

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: 'service_name', label: 'Service Name', numeric: false },
    { id: 'avg_requests', label: 'Avg Requests', numeric: true },
    { id: 'percentage_vs_avg_error', label: 'Error trend', numeric: true, tooltip: 'Error variation in the last day selected with respect to the average' },
    { id: 'percentage_vs_avg_latency', label: 'Latency trend', numeric: true, tooltip: 'Latency variation in the last day selected with respect to the average' },
    { id: 'percentage_vs_avg', label: 'Requests Trend', numeric: true, tooltip: 'Requests variation in the last day selected with respect to the average' },
];

function formatNumber(num, maxFractionDigits = 2, minFractionDigits = 0) {
    return new Intl.NumberFormat('es-ES', {
        maximumFractionDigits: maxFractionDigits,
        minimumFractionDigits: minFractionDigits
    }).format(num);
}

function formatPercentage(num) {
    return new Intl.NumberFormat('es-ES', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        signDisplay: 'always'
    }).format(num);
}

function formatDuration(ms) {
    if (ms == null || isNaN(ms)) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1).replace(/\.0$/, '')}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1).replace(/\.0$/, '')}m`;
    return `${(ms / 3600000).toFixed(1).replace(/\.0$/, '')}h`;
}

function PercentageTrendCell({ value, statusColor, sx }) {
    return (
        <Box
            sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: statusColor ? theme.palette[statusColor]?.main || statusColor : 'inherit',
                ...sx,
            })}
        >
            {value !== null ? (
                <>
                    {value !== 0 && (
                        <IconComponent iconName={value > 0 ? 'arrow-up' : 'arrow-down'} />
                    )}
                    <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>{Math.abs(formatPercentage(value))}%</Typography>
                </>
            ) : (
                <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>-</Typography>
            )}
        </Box>
    );
}

export default function MuiServiceTable(props) {
    const {
        serviceData = [],
        isLoadingServiceData = false,
        title = 'Service Metrics',
        subtitle = 'Overview of service performance metrics',
        serviceSelected = [],
    } = props;

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('service_name');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [expanded, setExpanded] = useState({});

    const filteredData = useMemo(() => {
        if (serviceSelected?.length > 0) {
            return serviceData.filter(service => serviceSelected.includes(service.service_name));
        }
        return serviceData;
    }, [serviceData, serviceSelected]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExpandClick = (serviceName) => {
        setExpanded(prev => ({ ...prev, [serviceName]: !prev[serviceName] }));
    };

    const sortedData = useMemo(() => stableSort(filteredData, getComparator(order, orderBy)), [filteredData, order, orderBy]);
    const currentData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const renderMetadata = (service) => {
        const metadata = [
            { label: 'Avg Requests Error', value: service.avg_requests_error || '-' },
            { label: 'Max Requests Error', value: service.max_requests_error || '-' },
            { label: 'Min Requests Error', value: service.min_requests_error || '-' },

            { label: 'Min Requests', value: service.min_requests || '-' },
            { label: 'Max Requests', value: service.max_requests || '-' },

            { label: 'Avg Cloudsql Latency', value: formatDuration(service.avg_cloudsql_latency) },
            { label: 'Max Cloudsql Latency', value: formatDuration(service.max_cloudsql_latency) },
            { label: 'Min Cloudsql Latency', value: formatDuration(service.min_cloudsql_latency) },

            { label: 'Avg Latency', value: formatDuration(service.avg_latency) },
            { label: 'Max Latency', value: formatDuration(service.max_latency) },
            { label: 'Min Latency', value: formatDuration(service.min_latency) },
        ];
        return (
            <Box sx={{ p: 2, backgroundColor: '#e9e9f1', borderRadius: '5px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                    {metadata.map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                            <Typography variant="body2">{item.value}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Card sx={{ p: 2 }}>
                <Box className="text-center">
                    <Typography variant="h6">{title}</Typography>
                    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                </Box>
                {isLoadingServiceData ? (
                    <Skeleton variant="rectangular" height={350} />
                ) : (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 40 }} />
                                        {headCells.map((headCell) => (
                                            <TableCell
                                                key={headCell.id}
                                                align={headCell.numeric ? 'right' : 'left'}
                                                sortDirection={orderBy === headCell.id ? order : false}
                                            >
                                                <Tooltip title={headCell.tooltip || ''} arrow>
                                                    <TableSortLabel
                                                        active={orderBy === headCell.id}
                                                        direction={orderBy === headCell.id ? order : 'asc'}
                                                        onClick={(e) => handleRequestSort(e, headCell.id)}
                                                    >
                                                        <Typography fontWeight={headCell.id === orderBy ? 'bold' : 'normal'}>
                                                            {headCell.label}
                                                        </Typography>
                                                    </TableSortLabel>
                                                </Tooltip>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentData.map((service) => (
                                        <React.Fragment key={service.service_name}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExpandClick(service.service_name)}
                                                        aria-label={expanded[service.service_name] ? 'Collapse' : 'Expand'}
                                                    >
                                                        {expanded[service.service_name] ? <IconComponent iconName='chevron-down' /> : <IconComponent iconName='chevron-up' />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={service.service_cr_instance_link ? 'Click to view Cloud Run instance details' : ''}>
                                                        <MuiLink href={service.service_cr_instance_link ?? ''} target="_blank" underline="hover">
                                                            {service.service_name}
                                                        </MuiLink>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">{formatNumber(service.avg_requests, 0, 0)}</TableCell>
                                                <TableCell align="right">
                                                    <PercentageTrendCell value={service.percentage_vs_avg_error} statusColor={service.status_color_error} />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <PercentageTrendCell value={service.percentage_vs_avg_latency} statusColor={service.status_color_latency} />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <PercentageTrendCell value={service.percentage_vs_avg} statusColor={service.status_color} />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 'none' }} colSpan={headCells.length + 1}>
                                                    <Collapse in={expanded[service.service_name]} timeout="auto" unmountOnExit sx={{ mt: 1 }}>
                                                        {renderMetadata(service)}
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={filteredData.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{
                                '.MuiTablePagination-select': {
                                    borderRadius: '0.5rem',
                                },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    margin: 0,
                                }
                            }}
                        />
                    </>
                )}
            </Card>
        </Box>
    );
} 